"use client";

import { useEffect, useRef, useState } from "react";
import { useDraggable, type Position } from "@/hooks/ui";
import { cn } from "@/lib/utils";
import { RefreshCw } from "lucide-react";

type StaleVersionBannerProps = {
  onReload: () => void;
  onDismiss: () => void;
};

const BOUNDS = { padding: 4, elementSize: 60 };

function getDefaultPosition(): Position {
  // Offset below the API-health banner (which defaults to y=4) so the two
  // banners don't overlap when both happen to be visible.
  if (typeof window === "undefined") return { x: 12, y: 100 };
  return { x: Math.max(12, (window.innerWidth - 320) / 2), y: 100 };
}

function clampPosition(pos: Position): Position {
  if (typeof window === "undefined") return pos;
  const maxX = window.innerWidth - BOUNDS.elementSize;
  const maxY = window.innerHeight - BOUNDS.elementSize;
  return {
    x: Math.max(BOUNDS.padding, Math.min(pos.x, maxX)),
    y: Math.max(BOUNDS.padding, Math.min(pos.y, maxY)),
  };
}

export function StaleVersionBanner({ onReload, onDismiss }: StaleVersionBannerProps) {
  const [position, setPosition] = useState<Position>(getDefaultPosition);
  const containerRef = useRef<HTMLDivElement>(null);

  const { isDragging, handleMouseDown } = useDraggable({
    elementRef: containerRef,
    enabled: true,
    position,
    onPositionChange: setPosition,
    clampPosition,
    positionOrigin: "top-left",
    enableMouse: true,
  });

  useEffect(() => {
    const handleResize = () => setPosition((prev) => clampPosition(prev));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleReloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onReload();
  };

  const handleDismissClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss();
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "fixed z-snackbar left-0 top-0 p-3 w-[calc(100vw-2rem)] max-w-sm",
        "rounded-xl",
        "bg-cede-warning/10 backdrop-blur-md shadow-lg",
        "border border-cede-warning/30",
        "cursor-grab select-none will-change-transform",
        "transition-all duration-300 ease-out",
        isDragging && "cursor-grabbing",
      )}
      style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
      onMouseDown={handleMouseDown}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          <RefreshCw className="h-5 w-5 text-cede-warning" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-cede-text-primary">Update available</p>
          <p className="mt-1 text-xs text-cede-text-secondary">
            A newer version of Cede Hub is ready. Reload when convenient to avoid stale data.
          </p>

          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={handleReloadClick}
              className="rounded-lg bg-cede-warning px-3 py-1.5 text-xs font-medium text-cede-warning-foreground hover:bg-cede-warning/90 transition-colors"
            >
              Reload
            </button>
            <button
              onClick={handleDismissClick}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-cede-text-secondary hover:bg-cede-warning/10 transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
